import { USER_ROLE_LABELS, type UserRole } from "@bi/types";
import type { AttentionPoint } from "./intelligence/executive";

/**
 * Leituras de Governança que a tela mostra: quem está parado, quem ficou em
 * perfil antigo e como descrever cada evento do histórico em português.
 */
export const DORMANT_DAYS = 30;
export const LEGACY_ROLES: UserRole[] = ["gestor", "consultor", "visualizador"];

export type GovUser = {
  id: string;
  name: string;
  email: string;
  role: UserRole;
  status: "active" | "inactive" | "pending";
  lastLoginAt: string | null;
  createdAt: string;
};

const DAY = 86_400_000;

export function daysSince(iso: string, now: Date): number {
  return Math.max(0, Math.floor((now.getTime() - new Date(iso).getTime()) / DAY));
}

/** "hoje", "ontem", "há 5 dias", "há 3 meses". */
export function relativeDays(iso: string, now: Date): string {
  const d = daysSince(iso, now);
  if (d === 0) return "hoje";
  if (d === 1) return "ontem";
  if (d < 60) return `há ${d} dias`;
  const m = Math.round(d / 30);
  return m < 24 ? `há ${m} meses` : `há ${Math.round(d / 365)} anos`;
}

export type AccessState = { label: string; stale: boolean };

/** Último acesso de um usuário ativo, marcando quem nunca entrou ou está parado. */
export function accessState(u: Pick<GovUser, "lastLoginAt" | "status">, now: Date): AccessState {
  if (!u.lastLoginAt) return { label: "Nunca acessou", stale: u.status === "active" };
  return { label: relativeDays(u.lastLoginAt, now), stale: u.status === "active" && daysSince(u.lastLoginAt, now) >= DORMANT_DAYS };
}

export type GovSummary = {
  total: number;
  active: number;
  pending: number;
  admins: GovUser[];
  never: GovUser[];
  dormant: GovUser[];
  legacy: GovUser[];
};

export function summarizeUsers(users: GovUser[], now: Date): GovSummary {
  const active = users.filter((u) => u.status === "active");
  return {
    total: users.length,
    active: active.length,
    pending: users.filter((u) => u.status === "pending").length,
    admins: active.filter((u) => u.role === "admin"),
    never: active.filter((u) => !u.lastLoginAt),
    dormant: active.filter((u) => u.lastLoginAt && daysSince(u.lastLoginAt, now) >= DORMANT_DAYS),
    legacy: active.filter((u) => LEGACY_ROLES.includes(u.role)),
  };
}

/** "Ana, Bruno e mais 2" */
export function nameList(users: { name: string }[], max = 3): string {
  const names = users.map((u) => u.name);
  if (names.length <= max) return names.length > 1 ? `${names.slice(0, -1).join(", ")} e ${names.at(-1)}` : names[0] ?? "";
  return `${names.slice(0, max).join(", ")} e mais ${names.length - max}`;
}

const STATUS_LABEL: Record<string, string> = { active: "Ativo", inactive: "Inativo", pending: "Pendente" };
const FIELD_LABEL: Record<string, string> = { name: "nome", whatsappNumber: "WhatsApp", role: "perfil", status: "status" };

export type AuditDetails = {
  before?: { role: string; status: string };
  after?: { role: string; status: string };
  role?: string;
  changed?: string[];
} | null;

const roleLabel = (r: string) => USER_ROLE_LABELS[r as UserRole] ?? r;
const statusLabel = (s: string) => STATUS_LABEL[s] ?? s;

/** Texto da coluna "Alteração" do histórico. */
export function describeChange(action: string, details: AuditDetails): string {
  if (!details) return "—";
  if (action === "user.updated" && details.before && details.after) {
    const parts: string[] = [];
    if (details.before.role !== details.after.role) parts.push(`Perfil ${roleLabel(details.before.role)} → ${roleLabel(details.after.role)}`);
    if (details.before.status !== details.after.status) parts.push(`${statusLabel(details.before.status)} → ${statusLabel(details.after.status)}`);
    const other = (details.changed ?? []).filter((f) => f !== "role" && f !== "status").map((f) => FIELD_LABEL[f] ?? f);
    if (other.length) parts.push(`Alterou ${other.join(" e ")}`);
    return parts.length ? parts.join(" · ") : "Sem mudança de perfil ou status";
  }
  if (details.role) return `Perfil ${roleLabel(details.role)}`;
  return "—";
}

/**
 * Fila "O que pede ação" da Governança, no formato da Visão Executiva: quem
 * nunca entrou, quem parou de entrar, quem ficou em perfil antigo e excesso de
 * administradores.
 */
export function governanceAttention(s: GovSummary): AttentionPoint[] {
  const pts: Omit<AttentionPoint, "priority">[] = [];
  const names = (list: GovUser[]) => list.map((u) => ({ label: u.name, value: u.lastLoginAt ? `último acesso ${new Date(u.lastLoginAt).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" })}` : "nunca acessou" }));
  if (s.never.length) pts.push({
    id: "nunca-acessou", severity: "warning", memory: null,
    title: s.never.length > 1 ? "Usuários que nunca acessaram" : "Usuário que nunca acessou",
    fact: `${nameList(s.never)} ${s.never.length > 1 ? "têm acesso ativo e nunca entraram" : "tem acesso ativo e nunca entrou"} no painel.`,
    impact: `${s.never.length} conta${s.never.length > 1 ? "s" : ""} sem uso`,
    action: "Confirme se ainda precisa do acesso; se não, inative na aba Usuários.",
    evidence: names(s.never),
  });
  if (s.dormant.length) pts.push({
    id: "sem-acesso", severity: "warning", memory: null,
    title: `Sem entrar há ${DORMANT_DAYS} dias ou mais`,
    fact: `${nameList(s.dormant)} ${s.dormant.length > 1 ? "não entram" : "não entra"} no painel há ${DORMANT_DAYS} dias ou mais.`,
    impact: `${s.dormant.length} acesso${s.dormant.length > 1 ? "s" : ""} parado${s.dormant.length > 1 ? "s" : ""}`,
    action: "Acesso parado é acesso a revisar: confirme com a pessoa ou inative.",
    evidence: names(s.dormant),
  });
  if (s.legacy.length) pts.push({
    id: "perfil-antigo", severity: "info", memory: null,
    title: "Perfis antigos ainda em uso",
    fact: `${nameList(s.legacy)} ainda ${s.legacy.length > 1 ? "estão" : "está"} em perfil antigo (${[...new Set(s.legacy.map((u) => roleLabel(u.role)))].join(", ")}).`,
    impact: `${s.legacy.length} usuário${s.legacy.length > 1 ? "s" : ""}`,
    action: "Mova para Admin ou Manutenção na aba Usuários.",
    evidence: s.legacy.map((u) => ({ label: u.name, value: roleLabel(u.role) })),
  });
  if (s.admins.length > 3) pts.push({
    id: "muitos-admins", severity: "info", memory: null,
    title: "Muitos administradores",
    fact: `${s.admins.length} contas ativas têm acesso total, inclusive à gestão de usuários.`,
    impact: `${s.admins.length} administradores`,
    action: "Quanto menos contas com acesso total, menor o risco. Revise quem precisa ser Admin.",
    evidence: s.admins.map((u) => ({ label: u.name, value: u.email })),
  });
  return pts.map((p, i) => ({ ...p, priority: i + 1 }));
}
